import { useMemo, useCallback, useEffect, useRef } from "react";
import {
  TextInput,
  Stack,
  Select,
  SimpleGrid,
  Switch,
  Checkbox,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Physician } from "../../../services/admin/physicians.service";
import { usePathologists } from "../../../hooks/useAdmin";
import { useActiveReportTemplates } from "../../../hooks/admin/useReportTemplates";
import {
  scrollToFirstError,
  required,
  npi,
  phone,
  fax,
  email,
} from "../../../utils/formValidation";

export interface PhysicianFormValues {
  firstName: string;
  lastName: string;
  npi: string;
  phone: string;
  fax: string;
  email: string;
  preferredPathologistId: string;
  reportTemplateId: string;
  isActive: boolean;
  offerTissueCypher: boolean;
  isRemote: boolean;
  remoteShippingStreet: string;
  remoteShippingCity: string;
  remoteShippingState: string;
  remoteShippingZip: string;
}

interface PhysicianFormProps {
  initialValues?: Physician;
  onSubmit: (values: PhysicianFormValues) => void;
  /** DOM id for the underlying <form>, so an external submit button can target it.
   *  Must be unique when more than one PhysicianForm is mounted at once. */
  formId?: string;
}

export default function PhysicianForm({
  initialValues,
  onSubmit,
  formId = "physician-form",
}: PhysicianFormProps) {
  const { data: pathologists = [] } = usePathologists();
  const { data: reportTemplates = [] } = useActiveReportTemplates();
  const pathologistOptions = useMemo(
    () =>
      pathologists
        .map((p) => ({ value: p.id, label: p.full_name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [pathologists]
  );

  const form = useForm<PhysicianFormValues>({
    initialValues: {
      firstName: initialValues?.firstName || "",
      lastName: initialValues?.lastName || "",
      npi: initialValues?.npi || "",
      phone: initialValues?.phone || "",
      fax: initialValues?.fax || "",
      email: initialValues?.email || "",
      preferredPathologistId: initialValues?.preferredPathologistId || "",
      reportTemplateId: initialValues?.reportTemplateId || "",
      isActive: initialValues?.isActive ?? true,
      offerTissueCypher: initialValues?.offerTissueCypher ?? false,
      isRemote: initialValues?.isRemote ?? false,
      remoteShippingStreet: initialValues?.remoteShippingStreet || "",
      remoteShippingCity: initialValues?.remoteShippingCity || "",
      remoteShippingState: initialValues?.remoteShippingState || "",
      remoteShippingZip: initialValues?.remoteShippingZip || "",
    },
    validate: {
      firstName: required("First name is required"),
      lastName: required("Last name is required"),
      npi: npi(),
      phone: phone(),
      fax: fax(),
      email: email(),
      remoteShippingStreet: (value, values) =>
        values.isRemote && !value ? "Street address is required" : null,
      remoteShippingCity: (value, values) =>
        values.isRemote && !value ? "City is required" : null,
      remoteShippingState: (value, values) =>
        values.isRemote && !value ? "State is required" : null,
      remoteShippingZip: (value, values) =>
        values.isRemote && !value ? "ZIP code is required" : null,
    },
  });

  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;
  const { setValues, clearErrors } = form;

  useEffect(() => {
    const initialValues = initialValuesRef.current;
    setValues({
      firstName: initialValues?.firstName || "",
      lastName: initialValues?.lastName || "",
      npi: initialValues?.npi || "",
      phone: initialValues?.phone || "",
      fax: initialValues?.fax || "",
      email: initialValues?.email || "",
      preferredPathologistId: initialValues?.preferredPathologistId || "",
      reportTemplateId: initialValues?.reportTemplateId || "",
      isActive: initialValues?.isActive ?? true,
      offerTissueCypher: initialValues?.offerTissueCypher ?? false,
      isRemote: initialValues?.isRemote ?? false,
      remoteShippingStreet: initialValues?.remoteShippingStreet || "",
      remoteShippingCity: initialValues?.remoteShippingCity || "",
      remoteShippingState: initialValues?.remoteShippingState || "",
      remoteShippingZip: initialValues?.remoteShippingZip || "",
    });
    clearErrors();
  }, [initialValues?.id, setValues, clearErrors]);

  const handleFormSubmit = useCallback(
    (values: PhysicianFormValues) => {
      onSubmit(values);
    },
    [onSubmit]
  );

  const handleValidationError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);

  const handleSubmit = useMemo(
    () => form.onSubmit(handleFormSubmit, handleValidationError),
    [form, handleFormSubmit, handleValidationError]
  );

  return (
    <form id={formId} noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="First Name"
            withAsterisk
            {...form.getInputProps("firstName")}
          />
          <TextInput
            label="Last Name"
            withAsterisk
            {...form.getInputProps("lastName")}
          />
          <TextInput
            label="NPI"
            placeholder="Enter NPI"
            {...form.getInputProps("npi")}
          />
          <TextInput
            label="Phone"
            placeholder="Enter phone number"
            {...form.getInputProps("phone")}
          />
          <TextInput
            label="Fax"
            placeholder="Enter fax number"
            {...form.getInputProps("fax")}
          />
          <TextInput
            label="Email"
            placeholder="Enter email address"
            type="email"
            {...form.getInputProps("email")}
          />
        </SimpleGrid>

        <Select
          label="Preferred Pathologist"
          placeholder="Select preferred pathologist"
          data={pathologistOptions}
          searchable
          clearable
          {...form.getInputProps("preferredPathologistId")}
        />

        <Select
          label="Report Template"
          placeholder="Select report template"
          data={reportTemplates
            .map((t) => ({
              value: t.id,
              label: t.isDefault ? `${t.name} (Default)` : t.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))}
          searchable
          clearable
          {...form.getInputProps("reportTemplateId")}
        />

        <Checkbox
          label="Offer Tissue Cypher"
          {...form.getInputProps("offerTissueCypher", { type: "checkbox" })}
        />

        <Divider my="xs" label="Remote Shipping Address" labelPosition="left" />

        <Stack gap="xs">
          <Switch
            label="Remote Physician"
            description="If enabled, cases from this physician will use this address for shipping workflows."
            color="blue"
            {...form.getInputProps("isRemote", {
              type: "checkbox",
            })}
          />

          {form.values.isRemote && (
            <Stack gap="sm">
              <TextInput
                label="Remote Shipping Street"
                placeholder="Enter street address"
                {...form.getInputProps("remoteShippingStreet")}
              />
              <SimpleGrid cols={3}>
                <TextInput
                  label="City"
                  placeholder="City"
                  {...form.getInputProps("remoteShippingCity")}
                />
                <TextInput
                  label="State"
                  placeholder="State"
                  {...form.getInputProps("remoteShippingState")}
                />
                <TextInput
                  label="ZIP Code"
                  placeholder="ZIP"
                  {...form.getInputProps("remoteShippingZip")}
                />
              </SimpleGrid>
            </Stack>
          )}
        </Stack>
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
